import { useState } from "react";
import MarkdownView from "./MarkdownView";
import API_BASE_URL from "../config";

const TYPE_BADGE = {
  page: "📄 Seite", table: "▦ Tabelle", figure: "🖼 Abbildung", reference: "🔗 Referenz",
};

// Highlight query terms in plain text (case-insensitive), returning React nodes.
function highlight(text, terms) {
  if (!text || !terms || terms.length === 0) return text;
  const safe = terms.filter(Boolean).map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (safe.length === 0) return text;
  const splitRe = new RegExp(`(${safe.join("|")})`, "gi");
  // Non-global regex for per-part membership test — a global regex's stateful
  // lastIndex makes .test() alternate true/false across calls, mis-highlighting.
  const matchRe = new RegExp(`^(${safe.join("|")})$`, "i");
  return text.split(splitRe).map((part, i) =>
    matchRe.test(part) ? <mark key={i}>{part}</mark> : <span key={i}>{part}</span>);
}

export default function SearchResultCard({ result, terms, onAskAboutThis }) {
  const [expanded, setExpanded] = useState(false);
  const r = result;
  // Text entities render their content as rich markdown (tables → tables,
  // $…$ → KaTeX). Clamped by default, unclamped when expanded.
  const hasRichContent =
    (r.type === "table" || r.type === "page" || r.type === "reference") && !!r.content;
  return (
    <div className="result-card">
      <div className="result-top">
        <div className="result-head">
          <span className="ds-badge">{TYPE_BADGE[r.type] || r.type}</span>
          <span className="doc-badge">{r.title || r.document_name}</span>
          <span className="doc-meta">Seite {r.page_number}</span>
          {r.chapter && <span className="doc-meta doc-chapter" dir="auto">{r.chapter}</span>}
        </div>
        <div className="result-score">{Number(r.score).toFixed(3)}</div>
      </div>

      <div className="result-body">
        {r.type === "figure" && r.image_url && (
          <button type="button" className="result-thumb" onClick={() => setExpanded((v) => !v)}
                  title="Abbildung vergrößern">
            <img src={`${API_BASE_URL}${r.image_url}`} alt={r.title} loading="lazy" />
          </button>
        )}
        {/* Clean plain-text teaser (markdown stripped server-side), with term highlights. */}
        <p className="result-text">{highlight(r.snippet, terms)}</p>
        {hasRichContent && (
          <div className={`result-content ds-markdown${expanded ? "" : " result-content--clamped"}`}>
            <MarkdownView>{r.content}</MarkdownView>
          </div>
        )}
      </div>

      {expanded && r.type === "figure" && r.image_url && (
        <div className="result-expanded">
          <a href={`${API_BASE_URL}${r.image_url}`} target="_blank" rel="noreferrer">
            <img className="result-expanded-img" src={`${API_BASE_URL}${r.image_url}`} alt={r.title} loading="lazy" />
          </a>
        </div>
      )}

      <div className="result-footer">
        <div className="result-actions">
          {(r.type === "figure" ? r.image_url : r.content) && (
            <button type="button" className="ds-btn ds-btn--ghost ds-btn--sm" onClick={() => setExpanded((v) => !v)}>
              {expanded ? "▲ Einklappen" : "▼ Anzeigen"}
            </button>
          )}
          {r.source_url && (
            <a className="ds-btn ds-btn--ghost ds-btn--sm" href={r.source_url} target="_blank" rel="noreferrer">
              ↗ Quelle öffnen
            </a>
          )}
        </div>
        <button type="button" className="ds-btn ds-btn--sm ds-btn--soft ask-btn" onClick={() => onAskAboutThis?.(r)}>
          Im Chat fragen →
        </button>
      </div>
    </div>
  );
}
